import { Component, OnInit, OnDestroy, ElementRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';

import { ContextService } from 'src/app/services/context.service';
import { AuthService } from 'src/app/services/auth.service';
import { IClinicContext } from 'src/app/models/clinic-context.model';

/**
 * ClinicSwitcherComponent
 *
 * Dropdown idêntico ao de módulos do sidebar.
 * - Se o usuário possui > 1 clínica: exibe dropdown interativo.
 * - Se o usuário possui == 1 clínica: exibe somente o nome em texto estático.
 * - Se o usuário possui 0 clínicas (ou loading): oculta o componente.
 *
 * Standalone — pode ser importado em qualquer layout (v2 sidebar, header legado, etc.).
 */
@Component({
  selector: 'app-clinic-switcher',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './clinic-switcher.component.html',
  styleUrls: ['./clinic-switcher.component.sass']
})
export class ClinicSwitcherComponent implements OnInit, OnDestroy {

  clinics: IClinicContext[] = [];
  activeClinic: IClinicContext | null = null;
  dropdownOpen = false;
  loading = true;
  searchTerm = '';
  isRoot = false;

  private clinicSub?: Subscription;

  constructor(
    private contextService: ContextService,
    private authService: AuthService,
    private elementRef: ElementRef,
    private router: Router
  ) {}

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.dropdownOpen = false;
    }
  }

  ngOnInit(): void {
    this.isRoot = this.authService.getPermission() === 'ROOT';

    // Assina a clínica ativa para manter o estado sincronizado
    this.clinicSub = this.contextService.getActiveClinic().subscribe((clinic) => {
      this.activeClinic = clinic;
    });

    // Se já carregou antes (ex: sidebar colapsou e expandiu), usa o cache
    if (this.contextService.isClinicsLoaded()) {
      this.clinics = this.contextService.getAvailableClinics();
      this.loading = false;
      return;
    }

    // Carrega a lista de clínicas disponíveis para o usuário
    this.contextService.loadClinicsAndSetDefault().subscribe({
      next: () => {
        this.clinics = this.contextService.getAvailableClinics();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.clinicSub?.unsubscribe();
  }

  /** Visibilidade: mostra se tem clínica ativa OU se já carregou */
  get isVisible(): boolean {
    if (this.activeClinic?.cnpj) return true;
    if (this.isRoot) return !this.loading;
    return !this.loading && this.clinics.length > 0;
  }

  /** Mostra dropdown somente se já carregou e (há mais de 1 clínica OU é ROOT) */
  get hasMultipleClinics(): boolean {
    if (this.loading) return false;
    if (this.isRoot) return true;
    return this.clinics.length > 1;
  }

  /** Label exibido no botão trigger */
  get triggerLabel(): string {
    if (this.isRoot && !this.activeClinic?.cnpj) {
      return 'ROOT';
    }
    return this.clinicDisplayName(this.activeClinic);
  }

  /** Verifica se está no modo ROOT (sem clínica selecionada) */
  get isRootMode(): boolean {
    return this.isRoot && !this.activeClinic?.cnpj;
  }

  /** Formata o nome de exibição da clínica: Nome - Estado - Bairro */
  clinicDisplayName(clinic: IClinicContext | null): string {
    if (!clinic) return 'Selecionar clínica';
    const parts = [clinic.name];
    if (clinic.state) parts.push(clinic.state);
    if (clinic.neighborhood) parts.push(clinic.neighborhood);
    return parts.join(' - ');
  }

  /** Mostra busca quando é ROOT ou tem mais de 5 clínicas */
  get showSearch(): boolean {
    return this.isRoot || this.clinics.length > 5;
  }

  /** Retorna clínicas filtradas pelo termo de busca */
  get filteredClinics(): IClinicContext[] {
    if (!this.searchTerm.trim()) return this.clinics;
    const term = this.searchTerm.toLowerCase().trim();
    return this.clinics.filter(c =>
      c.name.toLowerCase().includes(term) ||
      c.cnpj.includes(term) ||
      (c.state && c.state.toLowerCase().includes(term)) ||
      (c.neighborhood && c.neighborhood.toLowerCase().includes(term))
    );
  }

  toggleDropdown(): void {
    this.dropdownOpen = !this.dropdownOpen;
    if (this.dropdownOpen) {
      this.searchTerm = '';
    }
  }

  /** Seleciona o modo ROOT: limpa clínica ativa */
  selectRoot(): void {
    this.contextService.clearContext();
    this.dropdownOpen = false;
    this.router.navigate(['/main/v2']);
  }

  selectClinic(clinic: IClinicContext): void {
    if (clinic.cnpj === this.activeClinic?.cnpj) {
      this.dropdownOpen = false;
      return;
    }
    this.contextService.setActiveClinic(clinic);
    this.dropdownOpen = false;
    this.router.navigate(['/main/v2']);
  }

  isActive(clinic: IClinicContext): boolean {
    return clinic.cnpj === this.activeClinic?.cnpj;
  }
}
